import { CHILD_TYPE_ARRAY } from '../../../common/utils/childTypeConstants';
import { ElementCountMap } from '../../../common/elements/elementCountsUtils';
import { mapElementCountsToChildCounts } from '../../element/containerElement/containerElementUtil';

export type ChildTypeCountMap = {
    board: number;
    card: number;
    file: number;
    document: number;
};

export type ChildTypeCount = {
    type: string;
    count: number;
};

export const createEmptyChildTypeCountMap = (): ChildTypeCountMap => ({
    board: 0,
    card: 0,
    file: 0,
    document: 0,
});

export const toChildType = (type: string): keyof ChildTypeCountMap | undefined => {
    const lowerType = type.toLowerCase();
    return CHILD_TYPE_ARRAY.find((childType) => childType === lowerType);
};

/**
 * Checks if the counts object is already in ChildTypeCountMap format (with keys: board, card, file, document)
 * rather than ElementCountMap format (with ElementType keys).
 */
const isChildTypeCountMap = (counts: ElementCountMap | ChildTypeCountMap): counts is ChildTypeCountMap => {
    const keys = Object.keys(counts);

    // Empty object is treated as a child-type map
    if (keys.length === 0) {
        return true;
    }

    // Check if all keys are valid child type names
    return keys.every((key) => {
        const lowerKey = key.toLowerCase();
        return CHILD_TYPE_ARRAY.some((childType) => childType === lowerKey && key === lowerKey);
    });
};

/**
 * Normalizes either ElementCountMap or ChildTypeCountMap to a consistent ChildTypeCountMap.
 * Handles summing values if already in child-type format, or delegates to mapper if in element-type format.
 */
export const normaliseToChildCounts = (counts: ElementCountMap | ChildTypeCountMap): ChildTypeCountMap => {
    if (!counts || Object.keys(counts).length === 0) {
        return createEmptyChildTypeCountMap();
    }

    // If already in child-type format, sum the values
    if (isChildTypeCountMap(counts)) {
        return CHILD_TYPE_ARRAY.reduce<ChildTypeCountMap>((accumulator, key) => {
            const value = counts[key];

            if (typeof value === 'number' && Number.isFinite(value)) {
                accumulator[key] += value;
            }

            return accumulator;
        }, createEmptyChildTypeCountMap());
    }

    // Otherwise convert from ElementCountMap format
    return mapElementCountsToChildCounts(counts);
};
